import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, RefreshCcw } from "lucide-react";

const GOAL_OPTIONS = [
  "warm follow-up",
  "follow up on demo",
  "book a call",
  "re-engage cold lead",
  "check-in / status update",
  "close a deal",
];

export default function AIDraftEmailDialog({ open, onOpenChange, contactId, onSent }) {
  const [goal, setGoal] = useState("warm follow-up");
  const [tone, setTone] = useState("warm and direct");
  const [customGoal, setCustomGoal] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    setDraft(null);
    try {
      const { data } = await api.post("/drafts/generate", {
        contact_id: contactId,
        goal: customGoal || goal,
        tone,
      });
      setDraft(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Draft failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/drafts/${draft.id}`, { subject: draft.subject, body: draft.body });
      setDraft(data);
      toast.success("Saved to Outbox — review it there before sending");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!draft) return;
    if (!confirm(`Send this email to ${draft.contact_email}?`)) return;
    setBusy(true);
    try {
      // First save current edits, then send
      await api.put(`/drafts/${draft.id}`, { subject: draft.subject, body: draft.body });
      await api.post(`/drafts/${draft.id}/send`);
      toast.success("Email sent via Resend");
      if (onSent) onSent();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none max-w-2xl" data-testid="draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--peak-blue-deep)]" /> AI-DRAFT EMAIL
          </DialogTitle>
        </DialogHeader>

        {!draft && (
          <div className="space-y-4">
            <div>
              <label className="section-label">Goal</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {GOAL_OPTIONS.map((g) => (
                  <button
                    key={g}
                    data-testid={`goal-${g.replace(/\s+/g, "-")}`}
                    onClick={() => { setGoal(g); setCustomGoal(""); }}
                    className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${goal === g && !customGoal ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] hover:border-[var(--peak-ink)]"}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="section-label">Custom goal (optional)</label>
              <input data-testid="custom-goal" className="brutalist-input" placeholder="e.g. Get them to book a 15-min discovery call" value={customGoal} onChange={(e) => setCustomGoal(e.target.value)} />
            </div>
            <div>
              <label className="section-label">Tone</label>
              <input data-testid="tone" className="brutalist-input" value={tone} onChange={(e) => setTone(e.target.value)} />
            </div>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <button onClick={generate} disabled={busy} data-testid="draft-generate" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Drafting…" : "Generate draft"}
              </button>
            </DialogFooter>
          </div>
        )}

        {draft && (
          <div className="space-y-3">
            <div>
              <label className="section-label">Subject</label>
              <input data-testid="draft-subject" className="brutalist-input font-heading font-bold text-lg" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
            <div>
              <label className="section-label">Body</label>
              <textarea data-testid="draft-body" rows={10} className="w-full border border-[var(--peak-border)] p-3 text-sm focus:outline-none focus:border-[var(--peak-ink)]" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            </div>
            <div className="text-xs text-[var(--peak-muted)]">To: <span className="font-mono">{draft.contact_email}</span></div>
            <DialogFooter className="flex gap-2 sm:justify-end flex-wrap">
              <button onClick={generate} disabled={busy} data-testid="draft-regenerate" className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-3 inline-flex items-center gap-2 disabled:opacity-60">
                <RefreshCcw className="w-4 h-4" /> Regenerate
              </button>
              <button onClick={save} disabled={busy} data-testid="draft-save-outbox" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-3">
                Save to Outbox
              </button>
              <button onClick={sendNow} disabled={busy} data-testid="draft-send-now" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" /> Approve & send now
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
